import React, { useMemo, useState } from "react";
import "./EmailModalNew.scss";
import pdfFile from "./Medikamentenplan_Max_Mustermann.pdf";
import PDFPreviewModal from "./PDFPreviewModal";

type PipelineStep = "upload" | "ocr" | "classification" | "field_detection" | "validation";
type Phase = "idle" | "processing" | "review" | "done";

interface EmailActivity {
  sender: string;
  recipient: string;
  time: string;
  subject: string;
  body: string;
  attachment?: {
    name: string;
    file: string;
  };
}

interface MedicationItem {
  id: string;
  name: string;
  dose: string;
  morning: boolean;
  noon: boolean;
  evening: boolean;
  night: boolean;
  note?: string; // V/Z/N etc.
  confidence: number; // 0..100 (Extraktion)
}

interface ExtractResult {
  patientName: string;
  planDate?: string; // ISO (optional wenn gefunden)
  items: MedicationItem[];
  deviations?: string[]; // z.B. "abweichend zum Profil ..."
}

const getCurrentDateTime = (): string =>
  new Date().toLocaleString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

const email: EmailActivity = {
  time: getCurrentDateTime(),
  sender: "Max Mustermann",
  recipient: "Peter Meier",
  subject: "Re: Bestätigung unseres Termins am 03.02.2026",
  body:
    "Sehr geehrter Herr Meier,\n\nvielen Dank für Ihre E-Mail und die Terminbestätigung.\nAnbei sende ich Ihnen bereits den aktuellen Medikamentenplan als PDF.\n\nMit freundlichen Grüßen,\nMax Mustermann",
  attachment: {
    name: "Medikamentenplan.pdf",
    file: pdfFile,
  },
};

interface EmailModalProps {
  onClose: () => void;
  /** optional: Callback wenn der Nutzer die extrahierten Daten "übernimmt" */
  onAcceptExtract?: (result: ExtractResult) => void;
}

const PmockPDF: React.FC<{ file: string; fileName: string }> = ({ file, fileName }) => {
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const handlePreview = () => {
    setIsPreviewOpen(true);
  };

  return (
    <>
      <div className="pmockpdf" onClick={handlePreview} title="PDF anzeigen">
        <span>📎 {fileName}</span>
      </div>
      <PDFPreviewModal
        file={file}
        fileName={fileName}
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
      />
    </>
  );
};

/** Dummy-Generator für einen realistischen Medikationsplan */
function generateDummyMedicationExtract(): ExtractResult {
  const items: MedicationItem[] = [
        {
      id: "m2",
      name: "Metformin",
      dose: "1000 mg",
      morning: true,
      noon: false,
      evening: true,
      night: false,
      note: "N", // nach der Mahlzeit
      confidence: 89,
    },
    {
      id: "m1",
      name: "Ramipril",
      dose: "5 mg",
      morning: true,
      noon: false,
      evening: false,
      night: false,
      note: "Z", // zur Mahlzeit
      confidence: 94,
    },
    // 400 Ibuprofen
    {
      id: "m4",
      name: "Ibuprofen",
      dose: "400 mg",
      morning: false,
      noon: false,
      evening: true,
      night: false,
      note: "N", // nach der Mahlzeit
      confidence: 90,
    },
    // 20 Pantoprazol
    {
      id: "m5",
      name: "Pantoprazol",
      dose: "20 mg",
      morning: true,
      noon: false,
      evening: false,
      night: false,
      note: "V", // vor der Mahlzeit
      confidence: 92,
    },
    {
      id: "m3",
      name: "Acetylsalicylsäure (ASS)",
      dose: "100 mg",
      morning: false,
      noon: false,
      evening: true,
      night: false,
      note: "V", // vor der Mahlzeit
      confidence: 92,
    },
  ];

  const deviations: string[] = Math.random() > 0.6 ? ["Abweichend zum Profil: Kasse war 'TK'"] : [];

  return {
    patientName: "Max Mustermann",
    planDate: new Date().toISOString().slice(0, 10),
    items,
    deviations,
  };
}

const STEPS: PipelineStep[] = ["upload", "ocr", "classification", "field_detection", "validation"];

const EmailModal: React.FC<EmailModalProps> = ({ onClose, onAcceptExtract }) => {
  const [reply, setReply] = useState("");
  const [phase, setPhase] = useState<Phase>("idle");
  const [currentStep, setCurrentStep] = useState<PipelineStep | null>(null);
  const [progress, setProgress] = useState<number>(0);
  const [result, setResult] = useState<ExtractResult | null>(null);

  const isProcessing = phase === "processing";
  const isReview = phase === "review";
  const isDone = phase === "done";

  const handleExtract = async () => {
    if (!email.attachment) return;
    setPhase("processing");
    setProgress(0);

    // Simuliere die Pipeline analog DocumentCenter
    for (let i = 0; i < STEPS.length; i++) {
      const step = STEPS[i];
      setCurrentStep(step);
      // simulierte Schrittzeit (1.0-2.0 s)
      const stepTime = 1000 + Math.round(Math.random() * 1000);
      // weicher Fortschritt
      const target = Math.round(((i + 1) / STEPS.length) * 100);

      const start = progress;
      const delta = target - start;
      const ticks = Math.max(6, Math.round(stepTime / 120));
      for (let t = 1; t <= ticks; t++) {
        await new Promise((r) => setTimeout(r, Math.round(stepTime / ticks)));
        setProgress(start + Math.round((delta * t) / ticks));
      }
    }

    // Ergebnis erzeugen
    const extracted = generateDummyMedicationExtract();
    setResult(extracted);
    setCurrentStep(null);
    setPhase("review");
  };

  const avgConfidence = useMemo(() => {
    if (!result?.items?.length) return 0;
    const sum = result.items.reduce((s, i) => s + i.confidence, 0);
    return Math.round(sum / result.items.length);
  }, [result]);

  const handleAccept = () => {
    if (result) {
      onAcceptExtract?.(result);
    }
    setPhase("done");
  };

  const handleReset = () => {
    setPhase("idle");
    setCurrentStep(null);
    setProgress(0);
    setResult(null);
  };

  return (
    <div className="new-email-modal-overlay">
      <div className="new-email-modal modern-card">
        {/* Header */}
        <div className="new-email-modal-header">
          <div className="email-info">
            <span>Von: {email.sender}</span> | <span>An: {email.recipient}</span> |{" "}
            <span>Uhrzeit: {email.time}</span>
          </div>
          <i className="fa-solid fa-close" onClick={onClose} />
        </div>

        {/* Body */}
        <div className="new-email-modal-body">
          <h3 className="email-subject">{email.subject}</h3>
          <pre className="email-body">{email.body}</pre>

          {email.attachment && (
            <div className="email-attachment-row">
              <PmockPDF file={email.attachment.file} fileName={email.attachment.name} />
              <button
                className="btn btn-primary btn-medium-small"
                onClick={handleExtract}
                disabled={isProcessing || isReview || isDone}
              >
                Informationen auslesen
              </button>
            </div>
          )}

          {/* Processing UI */}
          {isProcessing && (
            <div className="processing-panel">
              <div className="steps-row">
                {STEPS.map((s) => {
                  const idx = STEPS.indexOf(s);
                  const activeIdx = currentStep ? STEPS.indexOf(currentStep) : -1;
                  const state =
                    activeIdx > idx ? "done" : activeIdx === idx ? "active" : "todo";
                  return (
                    <span key={s} className={`step-chip ${state}`}>
                      {s === "upload" && "Upload"}
                      {s === "ocr" && "OCR"}
                      {s === "classification" && "Klassifizierung"}
                      {s === "field_detection" && "Felderkennung"}
                      {s === "validation" && "Validierung"}
                    </span>
                  );
                })}
              </div>
              <div className="progressbar">
                <div className="fill" style={{ width: `${progress}%` }} />
              </div>
              <div className="progress-meta">{progress}%</div>
            </div>
          )}

          {/* Review UI */}
          {isReview && result && (
            <div className="new-email-modal-overlay">
              <div className="new-email-modal">
                <div className="review-panel">
                  <div className="result-header">
                    <div className="left">
                      <div className="result-title">Extrahierte Informationen</div>
                      <div className="result-sub">
                        Patient: <b>{result.patientName}</b>
                        {result.planDate ? <> • Datum: <b>{result.planDate}</b></> : null}
                        <br />
                        Durchschnittliche Konfidenz:{" "}
                        <span className={`conf-chip ${avgConfidence >= 90 ? "high" : avgConfidence >= 70 ? "mid" : "low"}`}>
                          {avgConfidence}%
                        </span>
                      </div>
                    </div>
                    <div className="right">
                      <button className="btn btn-success btn-medium-small" onClick={handleAccept}>
                        Übernehmen
                      </button>
                    </div>
                  </div>


                  <div className="med-table-wrap">
                    <table className="med-table">
                      <thead>
                        <tr>
                          <th style={{ width: "36%" }}>Medikament</th>
                          <th style={{ width: "16%" }}>Dosis</th>
                          <th>M</th>
                          <th>Mi</th>
                          <th>A</th>
                          <th>N</th>
                          <th style={{ width: "12%" }}>Konf.</th>
                        </tr>
                      </thead>
                      <tbody>
                        {result.items.map((m) => (
                          <tr key={m.id}>
                            <td>{m.name}</td>
                            <td>{m.dose}</td>
                            <td className="tcenter">{m.morning ? "●" : "–"}</td>
                            <td className="tcenter">{m.noon ? "●" : "–"}</td>
                            <td className="tcenter">{m.evening ? "●" : "–"}</td>
                            <td className="tcenter">{m.night ? "●" : "–"}</td>
                            <td>
                              <span
                                className={`conf-chip ${m.confidence >= 90 ? "high" : m.confidence >= 70 ? "mid" : "low"}`}
                                title="Konfidenz der Erkennung"
                              >
                                {m.confidence}%
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="hint">
                    Prüfen Sie die erkannten Angaben und klicken Sie auf <b>Übernehmen</b>, um sie in das Klientenprofil zu übernehmen.
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Done UI */}
          {isDone && (
            <div className="done-panel">
              <div className="done-emoji">✅</div>
              <div className="done-title">Daten übernommen</div>
              <div className="done-sub">Die extrahierten Informationen wurden übernommen.</div>
            </div>
          )}
        </div>

        {/* Reply */}
        <div className="new-email-modal-reply">
          <textarea
            className="reply-textarea"
            placeholder="Ihre Antwort..."
            value={reply}
            onChange={(e) => setReply(e.target.value)}
          />
          <button className="send-button btn btn-primary btn-medium-small">Senden</button>
        </div>
      </div>
    </div>
  );
};

export default EmailModal;
